import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import type { CreditBundle } from '@lr/shared';
import { apiFetch } from '../lib/api';
import { useAuth } from '../context/AuthContext';

/**
 * Credits screen — bundle picker. The actual Stripe charge is gated behind
 * ENABLE_STRIPE_PAYMENTS on the API; until that's flipped on, the CTA links to
 * Contact rather than charging the card. Visual still mirrors the design spec
 * (bundle list → "MOST POPULAR" badge on middle option → continue CTA).
 */
export function Credits() {
  const { user, profile } = useAuth();
  const navigate = useNavigate();
  const [bundles, setBundles] = useState<CreditBundle[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedId, setSelectedId] = useState<string | null>(null);

  useEffect(() => {
    apiFetch<CreditBundle[]>('/api/catalog/credit-bundles')
      .then((b) => {
        setBundles(b);
        // Default-select the middle bundle if none picked yet.
        if (b.length && !selectedId) {
          const middle = b[Math.floor(b.length / 2)];
          if (middle) setSelectedId(middle.id);
        }
      })
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const popularId = bundles.length ? bundles[Math.floor(bundles.length / 2)].id : null;
  const selected = bundles.find((b) => b.id === selectedId);

  function handleContinue() {
    if (!user) {
      navigate('/sign-up');
      return;
    }
    if (!selected) return;
    navigate(`/credits/payment?bundleId=${selected.id}`);
  }

  return (
    <section className="screen">
      <div className="scroll narrow-scroll">
        <div className="ck-head">
          <h1>ADD CREDITS</h1>
          <div className="p">Choose a bundle.</div>
        </div>

        {profile && (
          <div
            style={{
              margin: '0 16px 14px',
              padding: '14px 18px',
              background: 'var(--card)',
              border: '1px solid var(--line)',
              borderRadius: 'var(--radius)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 12
            }}
          >
            <div>
              <div
                style={{
                  fontSize: 9,
                  letterSpacing: '0.16em',
                  color: 'var(--muted)',
                  fontWeight: 700,
                  marginBottom: 4
                }}
              >
                CURRENT BALANCE
              </div>
              <div
                style={{
                  fontFamily: 'var(--display)',
                  fontSize: 24,
                  letterSpacing: '0.02em',
                  lineHeight: 1
                }}
              >
                {profile.credit_balance ?? 0}{' '}
                {profile.credit_balance === 1 ? 'CREDIT' : 'CREDITS'}
              </div>
            </div>
            <div
              style={{
                fontSize: 11,
                color: 'var(--ink-soft)',
                textAlign: 'right',
                lineHeight: 1.4
              }}
            >
              Top up below.
              <br />
              Credits never expire.
            </div>
          </div>
        )}

        {loading && <div style={{ padding: '20px 22px', color: 'var(--ink-soft)' }}>Loading bundles…</div>}

        <div>
          {bundles.map((b) => {
            const isSelected = b.id === selectedId;
            const isPopular = b.id === popularId;
            const pricePounds = (b.price_pence / 100).toFixed(0);
            const perCredit = (b.price_pence / 100 / b.credits).toFixed(2);
            return (
              <button
                key={b.id}
                className={`bundle${isSelected ? ' selected' : ''}`}
                onClick={() => setSelectedId(b.id)}
                type="button"
              >
                {isPopular && <span className="badge">MOST POPULAR</span>}
                <div className="info">
                  <div className="credits">
                    {b.credits} {b.credits === 1 ? 'CREDIT' : 'CREDITS'}
                  </div>
                  <div className="price">£{pricePounds}</div>
                  <div className="per">£{perCredit} per credit</div>
                  <div className="per free">£4.99 SHIPPING</div>
                </div>
                <div className="radio">
                  {isSelected && (
                    <svg
                      width="12"
                      height="12"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="3"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <polyline points="20 6 9 17 4 12" />
                    </svg>
                  )}
                </div>
              </button>
            );
          })}
        </div>

        <div className="info-strip">
          <Clock />
          <div>
            <div className="ttl">Credits never expire</div>
            <div className="sub">Use them whenever you want.</div>
          </div>
        </div>

        <div className="footer-cta">
          <button
            className={`cta-full${!selected ? ' disabled' : ''}`}
            disabled={!selected}
            onClick={handleContinue}
          >
            <span>{user ? 'CONTINUE TO PAYMENT' : 'CREATE ACCOUNT TO BUY'}</span>
            <Arrow />
          </button>
          <div
            className="micro"
            style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              gap: 6,
              marginTop: 10,
              fontSize: 11,
              color: 'var(--ink-soft)'
            }}
          >
            <Lock />
            Secure checkout
          </div>
        </div>

        {!user && (
          <div style={{ textAlign: 'center', padding: '16px 22px' }}>
            <Link
              to="/sign-in"
              style={{
                fontSize: 13,
                textDecoration: 'underline',
                textUnderlineOffset: 3,
                color: 'var(--ink)'
              }}
            >
              Already have an account? Sign in
            </Link>
          </div>
        )}
      </div>
    </section>
  );
}

function Arrow() {
  return (
    <svg
      width="20"
      height="20"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
    >
      <path d="M5 12h14M13 5l7 7-7 7" />
    </svg>
  );
}
function Clock() {
  return (
    <svg
      width="22"
      height="22"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
    >
      <circle cx="12" cy="12" r="9" />
      <path d="M12 7v5l3 2" />
    </svg>
  );
}
function Lock() {
  return (
    <svg
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.6"
    >
      <rect x="5" y="11" width="14" height="10" rx="2" />
      <path d="M8 11V7a4 4 0 018 0v4" />
    </svg>
  );
}
